import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { MEDIA_BREAKPOINTS } from '@/constants/layout';
import { useI18n } from '@/i18n/LanguageProvider';
import Icon from '@/components/StyledComponents/Icon';

/* ======================= FLAGS ======================= */

const LANG_FLAGS: Record<'he' | 'en' | 'ru' | 'fr', string> = {
  he: '🇮🇱',
  en: '🇺🇸',
  ru: '🇷🇺',
  fr: '🇫🇷',
};

const LANG_LABELS: Record<'he' | 'en' | 'ru' | 'fr', string> = {
  he: 'עברית',
  en: 'English',
  ru: 'Русский',
  fr: 'Français',
};

/* ======================= Shared ======================= */

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`;

type ButtonTone = 'default' | 'light';

const Trigger = styled.button<{
  $compactDesktop?: boolean;
  $tone: ButtonTone;
  $borderless?: boolean;
}>`
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: center;

  background-color: ${({ theme, $tone, $borderless }) =>
    $borderless || $tone === 'light' ? 'transparent' : theme.menuColor};
  color: ${({ theme, $tone }) =>
    $tone === 'light' ? '#f6f0ff' : theme.textColor};
  border: ${({ theme, $tone, $borderless }) =>
    $borderless
      ? '0'
      : `1px solid ${$tone === 'light' ? 'transparent' : theme.textColor}`};

  min-height: 38px;
  min-width: 44px;
  padding: 0 8px;

  cursor: pointer;
  border-radius: 18px;

  user-select: none;
  line-height: 1;
  box-shadow: none;

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    transition:
      transform 0.25s ease,
      box-shadow 0.25s ease;

    &:hover {
      transform: scale(1.15);
      background-color: ${({ theme, $tone, $borderless }) =>
        $borderless
          ? 'transparent'
          : $tone === 'light'
            ? 'rgba(255, 255, 255, 0.08)'
            : theme.menuColor};
      box-shadow: ${({ $tone, $borderless }) =>
        $borderless
          ? 'none'
          : $tone === 'light'
            ? '0 8px 18px rgba(0, 0, 0, 0.16)'
            : '0 4px 12px rgba(0, 0, 0, 0.25)'};
      border-radius: 50%;
    }
  }

  &:focus-visible {
    outline: 2px solid
      ${({ $tone }) =>
        $tone === 'light'
          ? 'rgba(184, 151, 255, 0.46)'
          : 'rgba(255, 255, 255, 0.72)'};
    outline-offset: 3px;
  }

  & > * {
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  ${({ $compactDesktop }) =>
    $compactDesktop
      ? `
      @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (max-width: 1120px) {
        min-height: 30px;
        min-width: 34px;
      }
    `
      : ''}
`;

const Flag = styled.span<{ $compactDesktop?: boolean }>`
  font-size: 28px;
  line-height: 1;
  display: inline-block;

  transform: translateY(1px);

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    font-size: 26px;
  }

  ${({ $compactDesktop }) =>
    $compactDesktop
      ? `
      @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (max-width: 1120px) {
        font-size: 22px;
      }
    `
      : ''}
`;

/* ======================= DESKTOP ======================= */

const DesktopOnly = styled.div`
  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    display: none;
  }
`;

const DesktopDropdown = styled.div<{ $tone: ButtonTone }>`
  position: fixed;

  display: flex;
  flex-direction: column;
  align-items: center;

  gap: 8px;

  background: ${({ theme, $tone }) =>
    $tone === 'light' ? 'rgba(18, 19, 26, 0.42)' : theme.menuColor};
  border: 1px solid
    ${({ theme, $tone }) =>
      $tone === 'light' ? 'rgba(184, 151, 255, 0.72)' : theme.textColor};
  border-radius: 14px;
  backdrop-filter: blur(10px);

  padding: 12px 10px;
  z-index: 9999;

  box-shadow: ${({ $tone }) =>
    $tone === 'light'
      ? '0 16px 36px rgba(0, 0, 0, 0.28)'
      : '0 12px 32px rgba(0, 0, 0, 0.35)'};

  min-width: 80px;
`;

/* ======================= MOBILE ======================= */

const MobileOnly = styled.div`
  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    display: none;
  }
`;

const MobileDropdown = styled.div<{ $tone: ButtonTone }>`
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);

  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  justify-items: center;

  background: ${({ theme, $tone }) =>
    $tone === 'light' ? 'rgba(18, 19, 26, 0.42)' : theme.menuColor};
  border: 1px solid
    ${({ theme, $tone }) =>
      $tone === 'light' ? 'rgba(184, 151, 255, 0.72)' : theme.textColor};
  border-radius: 12px;
  backdrop-filter: blur(10px);
  box-shadow: ${({ $tone }) =>
    $tone === 'light' ? '0 14px 28px rgba(0, 0, 0, 0.28)' : 'none'};

  width: 92px;
  max-width: calc(100vw - 24px);
  padding: 8px;
  box-sizing: border-box;
  z-index: 1200;
`;

/* ======================= OPTION ======================= */

const Option = styled.button`
  appearance: none;
  border: 0;
  font-size: 34px;

  line-height: 1;
  cursor: pointer;

  padding: 0;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0;
  background: transparent;

  transition:
    transform 0.15s ease,
    filter 0.15s ease;

  &:hover {
    transform: scale(1.15);
    filter: brightness(1.1);
  }

  &:focus-visible {
    outline: 2px solid rgba(184, 151, 255, 0.72);
    outline-offset: 3px;
  }
`;

/* ======================= Component ======================= */

type Lang = 'he' | 'en' | 'ru' | 'fr';

type Props = {
  onToggle?: () => void;
  compactDesktop?: boolean;
  tone?: ButtonTone;
  borderless?: boolean;
};

const ToggleLanguageButton: React.FC<Props> = ({
  onToggle,
  compactDesktop = false,
  tone = 'default',
  borderless = false,
}) => {
  const { lang, setLang } = useI18n();

  const [open, setOpen] = useState(false);

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const [pos, setPos] = useState({ top: 0, left: 0 });

  /* ----- Desktop position ----- */

  useEffect(() => {
    if (!open || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();

    setPos({
      top: rect.bottom + 8,
      left: rect.right - 70,
    });
  }, [open]);

  /* ----- Outside click ----- */

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    if (open) document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  /* ----- Select language ----- */

  const selectLanguage = (target: Lang) => {
    if (target !== lang) {
      setLang(target);
    }

    setOpen(false);
    onToggle?.();
  };

  const triggerLabel = lang === 'he' ? 'שינוי שפה' : 'Change language';

  return (
    <Wrapper ref={wrapperRef}>
      {/* CURRENT FLAG */}
      <Trigger
        type="button"
        ref={triggerRef}
        onClick={() => setOpen(p => !p)}
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        $compactDesktop={compactDesktop}
        $tone={tone}
        $borderless={borderless}
      >
        <Icon>
          <Flag $compactDesktop={compactDesktop}>{LANG_FLAGS[lang]}</Flag>
        </Icon>
      </Trigger>

      {/* ===== DESKTOP ===== */}
      <DesktopOnly>
        {open && (
          <DesktopDropdown
            role="menu"
            $tone={tone}
            style={{ top: pos.top, left: pos.left }}
          >
            {Object.entries(LANG_FLAGS).map(([key, flag]) => (
              <Option
                key={key}
                type="button"
                role="menuitem"
                aria-label={LANG_LABELS[key as Lang]}
                onClick={() => selectLanguage(key as Lang)}
              >
                {flag}
              </Option>
            ))}
          </DesktopDropdown>
        )}
      </DesktopOnly>

      {/* ===== MOBILE ===== */}
      <MobileOnly>
        {open && (
          <MobileDropdown role="menu" $tone={tone}>
            {Object.entries(LANG_FLAGS).map(([key, flag]) => (
              <Option
                key={key}
                type="button"
                role="menuitem"
                aria-label={LANG_LABELS[key as Lang]}
                onClick={() => selectLanguage(key as Lang)}
              >
                {flag}
              </Option>
            ))}
          </MobileDropdown>
        )}
      </MobileOnly>
    </Wrapper>
  );
};

export default ToggleLanguageButton;
